/**
 * Barra sticky de la PDP (boceto pdp-havaianas).
 *
 * Tres responsabilidades:
 *  1. Entrar/salir según si el CTA de la columna de info está en viewport
 *     (solo desktop; en mobile la barra está siempre).
 *  2. Ofrecer un dropdown de talles propio. El <select> nativo no sirve porque
 *     el botón "Agregar" de la barra tiene que poder abrirlo cuando falta el
 *     talle, y un select nativo no se abre por JS.
 *  3. Reflejar precio y miniatura al cambiar de variante.
 *
 * Los talles se leen del fieldset de la columna de info en cada apertura, no se
 * duplican desde Liquid: el stock por talle depende del color y ese picker se
 * parchea a mano sin re-render, así que el DOM vivo es la única fuente fiel.
 */
if (!customElements.get("sticky-atc-bar")) {
  customElements.define(
    "sticky-atc-bar",
    class StickyAtcBar extends HTMLElement {
      /**
       * El CDN de Shopify redimensiona por query param. `src` de la variante
       * puede venir con `?v=…`, así que se agrega respetando el separador.
       */
      static resizedImageUrl(src, width) {
        if (!src) return src;
        const separator = src.indexOf("?") === -1 ? "?" : "&";
        return `${src.split("&width=")[0]}${separator}width=${width}`;
      }

      connectedCallback() {
        this.productInfo = document.querySelector(
          `product-info[data-section="${this.dataset.section}"]`
        );
        this.picker = this.querySelector("[data-sticky-picker]");
        this.toggle = this.querySelector("[data-sticky-picker-toggle]");
        this.panel = this.querySelector(".sticky-atc-picker__panel");
        this.options = this.querySelector("[data-sticky-picker-options]");
        this.label = this.querySelector("[data-sticky-picker-label]");
        this.cta = this.querySelector("[data-sticky-add]");

        this.onDocumentClick = this.onDocumentClick.bind(this);

        if (this.toggle) {
          this.toggle.addEventListener("click", () => this.togglePanel());
        }
        if (this.cta) {
          this.cta.addEventListener("click", () => this.onAdd());
        }
        this.addEventListener("keydown", (event) => {
          if (event.key === "Escape") this.closePanel();
        });

        this.observeMainCta();
        this.syncLabel();

        if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS === "object") {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            (event) => this.onVariantChange(event)
          );
        }

        // El cambio de talle no publica variantChange hasta que la variante
        // resuelve, así que el label también escucha el picker directamente.
        if (this.productInfo) {
          this.productInfo.addEventListener("change", () => {
            this.syncLabel();
            this.classList.remove("has-size-error");
            this.closePanel();
          });
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        clearTimeout(this.outsideTimer);
        document.removeEventListener("click", this.onDocumentClick);
      }

      get submitButton() {
        return document.querySelector(this.dataset.submitSelector);
      }

      get sizeFieldset() {
        if (!this.productInfo) return null;
        return this.productInfo.querySelector(".variant-option-fieldset-size-js");
      }

      /**
       * En mobile la barra está siempre visible, así que no se observa nada: el
       * CTA principal puede estar en pantalla y la barra igual tiene que estar.
       */
      observeMainCta() {
        const button = this.submitButton;
        if (!button || !("IntersectionObserver" in window)) {
          this.classList.add("is-visible");
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.classList.toggle("is-visible", !entry.isIntersecting);
              if (entry.isIntersecting) this.closePanel();
            });
          },
          { threshold: 0 }
        );
        this.observer.observe(button);
      }

      onVariantChange(event) {
        if (!event || !event.data) return;
        if (event.data.sectionId !== this.dataset.section) return;

        const html = event.data.html;
        const price = this.querySelector("[data-sticky-price]");
        const source = html ? html.querySelector(".price-in-btn") : null;
        if (price && source) price.textContent = source.textContent.trim();

        const thumb = this.querySelector("[data-sticky-thumb]");
        const variant = event.data.variant;
        if (thumb && variant && variant.featured_image) {
          thumb.src = this.constructor.resizedImageUrl(
            variant.featured_image.src,
            132
          );
        }

        this.syncLabel();
      }

      /** Talles del picker de la columna de info, con su estado real. */
      readSizes() {
        const fieldset = this.sizeFieldset;
        if (!fieldset) return [];

        // Sin los talles ocultos por `custom.hide_variant` del color elegido
        // (VariantSelects#syncHiddenOptions los marca con data-variant-hidden).
        return Array.from(
          fieldset.querySelectorAll(
            'input[type="radio"]:not([data-variant-hidden])'
          )
        ).map((input) => ({
          value: input.value,
          checked: input.checked,
          disabled:
            input.disabled || input.classList.contains("disabled"),
          input,
        }));
      }

      syncLabel() {
        if (!this.label) return;
        const selected = this.readSizes().find((size) => size.checked);
        const strings = window.variantStrings || {};

        if (selected) {
          this.label.textContent = selected.value;
          this.label.classList.add("is-selected");
        } else {
          this.label.textContent = strings.chooseYourSize || "";
          this.label.classList.remove("is-selected");
        }
      }

      renderOptions() {
        if (!this.options) return;
        const strings = window.variantStrings || {};
        this.options.innerHTML = "";

        this.readSizes().forEach((size) => {
          const option = document.createElement("button");
          option.type = "button";
          option.className = "sticky-atc-picker__option";
          option.setAttribute("role", "option");
          option.setAttribute("aria-selected", String(size.checked));
          option.classList.toggle("is-selected", size.checked);
          option.classList.toggle("is-disabled", size.disabled);
          if (size.disabled) option.disabled = true;

          const value = document.createElement("span");
          value.textContent = size.value;
          option.appendChild(value);

          if (size.disabled) {
            const note = document.createElement("span");
            note.className = "sticky-atc-picker__note";
            note.textContent = strings.outOfStockShort || "";
            option.appendChild(note);
          }

          // Se clickea el radio real: dispara el `change` del que cuelgan
          // VariantSelects, el label del talle y el guard de product-info.
          option.addEventListener("click", () => {
            size.input.click();
            this.closePanel();
          });

          this.options.appendChild(option);
        });
      }

      togglePanel() {
        if (this.classList.contains("is-open")) this.closePanel();
        else this.openPanel();
      }

      openPanel() {
        if (!this.panel) return;
        this.renderOptions();
        this.panel.hidden = false;
        this.classList.add("is-open");
        if (this.toggle) this.toggle.setAttribute("aria-expanded", "true");

        // El listener se engancha recién en el próximo tick: el click que abrió
        // el panel todavía está burbujeando y llegaría a document, que lo leería
        // como "click afuera" y lo cerraría en el mismo gesto.
        clearTimeout(this.outsideTimer);
        this.outsideTimer = setTimeout(() => {
          document.addEventListener("click", this.onDocumentClick);
        }, 0);
      }

      closePanel() {
        if (!this.panel || !this.classList.contains("is-open")) return;
        this.panel.hidden = true;
        this.classList.remove("is-open");
        if (this.toggle) this.toggle.setAttribute("aria-expanded", "false");
        clearTimeout(this.outsideTimer);
        document.removeEventListener("click", this.onDocumentClick);
      }

      onDocumentClick(event) {
        if (this.picker && this.picker.contains(event.target)) return;
        this.closePanel();
      }

      /**
       * Sin talle: mensaje inline + shake arriba y se abre el dropdown de acá,
       * que es lo que el usuario tiene a mano. Con talle, se delega en el submit
       * de la columna de info.
       */
      onAdd() {
        const behavior = this.productInfo && this.productInfo.pdpBehavior;

        if (behavior && behavior.needsSize()) {
          behavior.flagMissingSize();
          this.classList.add("has-size-error");
          this.openPanel();
          return;
        }

        this.classList.remove("has-size-error");
        const button = this.submitButton;
        if (button) button.click();
      }
    }
  );
}
